import { ApiError, apiErrorReason } from '@/lib/api';
import { plural } from '@/lib/plural';

import type { Competency, Reader, TrainingRow } from './types';
import { formatDayMonth } from '@/lib/time';
import { errorAdvice } from '@/lib/error-advice';

// Presentación pura de la pantalla Capacitaciones (so=360): textos y tonos de las
// insignias y de la barra de avance. Sin enums crudos en la UI (UX-COPY-01).

export interface Badge {
  cls: 'b-ok' | 'b-warn' | 'b-red' | 'b-info';
  label: string;
}

/** Estado de lectura de un documento: completa, con vencidas o con pendientes. */
export function rowBadge(r: TrainingRow): Badge {
  if (r.pending === 0) {
    return { cls: 'b-ok', label: 'completa' };
  }
  if (r.overdue > 0) {
    return { cls: 'b-red', label: plural(r.overdue, 'vencida', 'vencidas') };
  }
  return { cls: 'b-warn', label: plural(r.pending, 'pendiente', 'pendientes') };
}

/** Color de la barra de avance, alineado con la insignia del documento. */
export function meterTone(r: TrainingRow): 'ok' | 'warn' | 'danger' {
  if (r.overdue > 0) {
    return 'danger';
  }
  return r.pending === 0 ? 'ok' : 'warn';
}

/** Porcentaje entero para mostrar; guion sin dato. */
export function formatPct(pct: number | null): string {
  return pct === null ? '—' : `${Math.round(pct)}%`;
}

const DISPATCH: Record<NonNullable<TrainingRow['dispatch']>['state'], Badge> = {
  sent: { cls: 'b-ok', label: 'Disparo enviado' },
  queued: { cls: 'b-info', label: 'Disparo en cola' },
  // Falló pero el sistema lo reintenta solo; el reintento manual es solo para 'failed'.
  retrying: { cls: 'b-warn', label: 'Reintentando envío' },
  failed: { cls: 'b-red', label: 'Disparo falló' },
  unlinked: { cls: 'b-warn', label: 'Sin vínculo al LMS' },
  // El LMS no admite la versión: la etapa sigue abierta y Calidad recibe el aviso (G2-C).
  version_gap: { cls: 'b-warn', label: 'Capacitación no disparada: el LMS no admite esta versión' },
  retired: { cls: 'b-info', label: 'Omitido: documento dado de baja' },
  returned: { cls: 'b-info', label: 'Aprobación devuelta' },
};

/** Insignia del último disparo al LMS (so=320); null si el documento no tuvo uno. */
export function dispatchBadge(d: TrainingRow['dispatch']): Badge | null {
  return d === null ? null : DISPATCH[d.state];
}

/** Estado de lectura de una persona; la fecha de lectura (día y mes) en la zona de negocio. */
export function readerBadge(r: Reader): Badge {
  if (r.state === 'read') {
    return {
      cls: 'b-ok',
      label: r.readAt === null ? 'Leído' : `Leído · ${formatDayMonth(r.readAt)}`,
    };
  }
  return r.state === 'overdue'
    ? { cls: 'b-red', label: 'Vencido' }
    : { cls: 'b-warn', label: 'Pendiente' };
}

const COMPETENCY: Record<Competency['status'], Badge> = {
  met: { cls: 'b-ok', label: 'Cumplida' },
  pending: { cls: 'b-warn', label: 'Pendiente' },
  expired: { cls: 'b-red', label: 'Vencida' },
};

export function competencyBadge(status: Competency['status']): Badge {
  return COMPETENCY[status];
}

/** Puesto principal y cuántos más ("Bodega +2"). */
export function positionsLabel(positions: string[]): string {
  const [first] = positions;
  if (first === undefined) {
    return 'Sin puesto en la empresa';
  }
  return positions.length > 1 ? `${first} +${positions.length - 1}` : first;
}

/** "12 asignados · LMS" (singular con 1). */
export function assignedLabel(n: number): string {
  return `${n.toLocaleString('es-CR')} ${n === 1 ? 'asignado' : 'asignados'} · LMS`;
}

/** Línea secundaria del indicador de pendientes. */
export function overdueNote(overdue: number): string {
  if (overdue === 0) {
    return 'lecturas sin completar';
  }
  return `lecturas sin completar · ${plural(overdue, 'vencida', 'vencidas')}`;
}

export interface ErrorCopy {
  title: string;
  body: string;
  retry: boolean;
}

/** Qué pasó y qué hacer según la respuesta del backend (UX-ERR-01). Solo se ofrece
 *  reintentar cuando reintentar puede resolverlo. */
export function errorCopy(err: unknown): ErrorCopy {
  if (err instanceof ApiError && err.status === 503) {
    return {
      title: 'El LMS no responde',
      body: 'El avance de lectura viene del LMS y no se pudo consultar. Vuelve a intentar en unos minutos.',
      retry: true,
    };
  }
  if (err instanceof ApiError && err.status === 409) {
    return {
      title: 'La empresa activa no está vinculada al LMS',
      body: 'Pide al administrador del sistema que vincule la empresa con el LMS para ver sus capacitaciones.',
      retry: false,
    };
  }
  if (err instanceof ApiError && apiErrorReason(err) === 'limit_exceeded') {
    return {
      title: 'Demasiados documentos para esta pantalla',
      body: 'La empresa supera el límite de documentos vigentes que la pantalla puede leer. Contacta a soporte.',
      retry: false,
    };
  }
  return {
    title: 'No se pudieron cargar las capacitaciones',
    body: errorAdvice(err),
    retry: true,
  };
}
